/**
 * Clipboard Handlers
 * Handles undo/redo for clipboard operations (paste, duplicate node/subtree)
 */

import type { Operation, NodeOperation } from '@/shared/types/projectData';
import type { OperationHandler } from '../types';

/**
 * Register all clipboard operation handlers
 */
export function registerClipboardHandlers(
  handlers: Map<string, OperationHandler>,
  applyOp: (op: NodeOperation) => Promise<void>
): void {
  // Paste nodes
  handlers.set('paste-nodes', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;

      if (op.cutNodeIds?.length && op.clipboardData) {
        const roots=new Set(op.sourceNodeIds||op.cutNodeIds);
        const originals=op.clipboardData.filter(node=>roots.has(node.id)).sort((a,b)=>a.order-b.order);
        for(const node of originals)await applyOp({type:'move',nodeId:node.id,targetParentId:node.parentId||null,targetIndex:node.order,skipHistory:true,timestamp:Date.now()});
        return;
      }

      // Remove pasted nodes
      if (op.duplicatedNodeMappings) {
        for (const mapping of op.duplicatedNodeMappings) {
          await applyOp({
            type: 'delete',
            nodeId: mapping.newId,
            skipHistory: true,
            timestamp: Date.now()
          });
        }
      }


    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        ...op,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });

  // Duplicate node
  handlers.set('duplicate-node', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (op.duplicatedNodeMappings?.[0]) {
        await applyOp({
          type: 'delete',
          nodeId: op.duplicatedNodeMappings[0].newId,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        ...op,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });

  // Duplicate subtree
  handlers.set('duplicate-subtree', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (op.duplicatedNodeMappings) {
        // Delete in reverse order to handle parent-child relationships
        for (let i = op.duplicatedNodeMappings.length - 1; i >= 0; i--) {
          await applyOp({
            type: 'delete',
            nodeId: op.duplicatedNodeMappings[i].newId,
            skipHistory: true,
            timestamp: Date.now()
          });
        }
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        ...op,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });
}
